import React, { useState, useRef } from 'react'
import T from 'prop-types'

import styles from './Styles.module.scss'
import BEMHelper from '../../helpers/bem'
import Button from '../Button'
import Icon from '../Icon'
import IllustrationGrid from '../IllustrationGrid'
import Collaborators from '../Collaborators'
import { getGrades, byline } from '../../helpers/utils'
import scrollTo from '../../helpers/scrollTo'

const bem = new BEMHelper(styles)

const getTags = ({ subjects }) => {
  const tags = []

  if (subjects?.length > 0) {
    subjects.forEach(({ title, value }) => {
      tags.push({
        label: title,
        as: `/?subjects=${value}&grades=grunnskole&grades=vgs`,
        href: {
          pathname: '/',
          query: {
            subjects: value,
            grades: ['grunnskole', 'vgs'],
          },
        },
      })
    })

    const grades = getGrades(subjects) || []
    grades.forEach(({ label, value }) => {
      tags.push({
        label,
        as: `/?grades=${value}`,
        href: {
          pathname: '/',
          query: {
            grades: value,
          },
        },
      })
    })
  }

  return tags
}

export default function Cover({
  title,
  purpose,
  preamble,
  illustration,
  muted,
  duration,
  sections,
  types,
  _type,
  collaborators,
  ...props
}) {
  const detailsRef = useRef()
  const [showDetails, setShowDetails] = useState(false)
  const [detailsHeight, setDetailsHeight] = useState('260px')
  const toggleDetails = () => {
    setShowDetails((value) => !value)

    if (detailsRef.current) {
      setDetailsHeight(`${detailsRef.current.scrollHeight}px`)
    }
  }
  const tags = getTags(props)
  const updated = byline(props)
  const student = _type === 'student'

  const handleStart = () => {
    const id = sections[0]?._key
    const element = document.getElementById(id)

    if (element) {
      const to = element?.getBoundingClientRect()?.top - 40
      scrollTo(to, 400)
    } else {
      const to = document.getElementById('cover')?.getBoundingClientRect()?.bottom + 40
      scrollTo(to, 400)
    }
  }

  const hasTypes =
    types && ((Object.keys(types) || []).filter((key) => key !== 'intro') || []).length > 0

  const showUpdated = !student && updated

  return (
    <header {...bem('', { muted })} id="cover">
      <div {...bem('box')}>
        <div {...bem('content')}>
          <h1 {...bem('title')}>{title}</h1>
          {duration?.from && (
            <span {...bem('duration')}>
              {duration?.from === duration?.to
                ? `${duration?.from} min`
                : `${duration?.from}-${duration?.to} min`}
            </span>
          )}
          <p {...bem('preamble')}>{purpose || preamble}</p>

          {student ? (
            <Button large primary label="Start" onClick={handleStart} {...bem('action')} />
          ) : (
            <nav {...bem('info')}>
              {tags.length > 0 && (
                <button type="button" onClick={toggleDetails} {...bem('more')}>
                  Fag og trinn
                  <Icon
                    icon="chevron"
                    direction={showDetails ? 'up' : 'down'}
                    {...bem('more-icon')}
                  />
                </button>
              )}

              {tags.length > 0 && (
                <div
                  {...bem('details', { expanded: showDetails })}
                  style={{ '--height': detailsHeight }}
                  ref={detailsRef}
                >
                  {tags.map(({ label, as, href }) => (
                    <Button key={as} label={label} as={as} href={href} tag {...bem('tag')} />
                  ))}
                </div>
              )}
            </nav>
          )}
        </div>

        <div {...bem('illustration', { topic: !types && illustration?.path })}>
          {hasTypes && <IllustrationGrid types={types} {...bem('types')} large />}

          {!hasTypes && illustration?.path && (
            <picture>
              {illustration.webp && <source srcSet={illustration.webp} type="image/webp" />}
              <source srcSet={illustration.path} type="image/png" />
              <img src={illustration.path} alt="" width={600} height={600 / illustration.aspect} />
            </picture>
          )}
        </div>
      </div>
      {(showUpdated || (collaborators && collaborators.length > 0)) && (
        <div {...bem('byline')}>
          {showUpdated && <div>Sist oppdatert {updated}.</div>}

          {collaborators && (
            <>
              {showUpdated && ' '}
              <Collaborators collaborators={collaborators} />.
            </>
          )}
        </div>
      )}
    </header>
  )
}

Cover.propTypes = {
  title: T.string.isRequired,
  purpose: T.string,
  preamble: T.string,
  illustration: T.shape({
    path: T.string.isRequired,
    aspect: T.number.isRequired,
  }),
  sections: T.array,
  muted: T.bool,
  _type: T.string,
  duration: T.shape({
    from: T.number.isRequired,
    to: T.number.isRequired,
  }),
  subjects: T.array,
  grades: T.object,
  topic: T.object,
  publishedAt: T.string,
  _updatedAt: T.string,
  author: T.object,
}
